import { formatValidation, getIslandLetter } from '../island-validation-format';
import { getSeamDemands } from '../../../breakpoints/helpers';
import { libLang } from '../../../../lang';
import { MAX_UNSEAMED_SECTION_LENGTH } from '../../../breakpoints/constants';
import { nearestSixteenth } from '../../../../helpers';
import { NotificationType } from '../../../notifications/enums';

import type { ActionRuleParams } from '../../types';
import type { Island } from '../../../islands/island.class';
import type { SeamDemand } from '../../../breakpoints/types';

const COUNTERTOP_SEAM_VALIDATION_CODE = libLang.validations.island.panel.countertopSeam;

const demandIssue = (demand: SeamDemand): string => {
	const { reason, section, maxSectionLength, anchorSku } = demand;

	if (reason === 'granite-appliance') {
		return `Granite countertop needs a breakpoint at ${anchorSku ?? 'this appliance'} — granite requires a seam at grills, C1SLV, farm sinks and refreshment centres at any countertop size`;
	}

	return `Countertop section ${nearestSixteenth(section.length)}" needs a breakpoint — a section cannot exceed ${maxSectionLength}"`;
};

const demandKey = (demand: SeamDemand): string => [demand.reason, demand.anchorItemId ?? '', demand.section.start, demand.section.end].join('|');

/**
 * Warns when a countertop run needs a seam it does not have.
 *
 * Concrete tops need one only where a section exceeds 88" (`guide-2026:208-212`); granite tops need
 * one at grills, C1SLV, farm sinks and refreshment centres at any size (`guide-2026:213-216`) as
 * well as respecting the same 88" cap. The cap measures the COUNTERTOP, not the base
 * (`guide-2026:436`). See `docs/breakpoint-rules.md`.
 */
export const countertopSeamLimit = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { islands } = refMap;
	const { notifications } = refMap.kitchen.design;

	notifications.purgeReferences(COUNTERTOP_SEAM_VALIDATION_CODE);

	if (!islands || !islands.children) return

	islands.children.forEach((child) => {
		const island = child as Island;
		const countertop = island.getCountertop();
		const frontPanel = island.panels?.getFrontPanel() ?? null;
		if (!countertop || !frontPanel) return

		const seen = new Set<string>();

		getSeamDemands({ countertop, maxSectionLength: MAX_UNSEAMED_SECTION_LENGTH }).forEach((demand) => {
			const key = demandKey(demand);
			if (seen.has(key)) return
			seen.add(key);

			notifications.add({
				refId: frontPanel.id,
				itemRefId: demand.anchorItemId,
				validationCode: COUNTERTOP_SEAM_VALIDATION_CODE,
				description: formatValidation({ letter: getIslandLetter(island), islandDescription: island.description, location: `${frontPanel.label} countertop`, issue: demandIssue(demand) }),
				type: NotificationType.Warn
			})
		})
	})
}
